'use client';
import React, { useState } from 'react';
import { useGed, userName } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { personnelReel } from '@/lib/comptes';
import { PROCESS_GROUPS, DEFAULT_PROCESSUS, sameProc, aujourdhuiIso } from '@/lib/meta';
import { I, card, inputStyle, useEscape, useConfirmation, SkeletonTiles, enChargement, Modale } from '@/components/ui';
import { H1 } from './commun';
import { Norme } from '../../Norme';
import type { ProcRow } from './commun';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, useDroppable,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
/** Même vocabulaire que le circuit léger de l'organigramme (Lot 4/5). */
const ETAT_CARTO_LABEL: Record<string, string> = {
  brouillon: 'brouillon', averifier: 'à vérifier', approuvee: 'approuvée',
};
interface CircuitCarto {
  version: string; etat: 'brouillon' | 'averifier' | 'approuvee';
  redacteur?: string; redigeLe?: string;
  verifiePar?: string; verifieLe?: string; approuvePar?: string; approuveLe?: string;
  dateApplication?: string;
}
const CIRCUIT_CARTO_DEFAUT: CircuitCarto = { version: '01', etat: 'approuvee' };

/**
 * LE BANDEAU DE CIRCUIT DE LA CARTOGRAPHIE — même gabarit que
 * `CircuitOrganigramme` (Organigramme.tsx). Un changement de bande ouvre ou
 * poursuit une révision (Lot 6) ; ce bandeau la fait avancer.
 */
function CircuitCartographie({ circuit, isRQ, moi, onFait, onErreur }: {
  circuit: CircuitCarto; isRQ: boolean;
  /** Comparé à `circuit.redacteur`/`verifiePar` — jamais proposer un acte que
   *  le serveur refusera (ISO 15189 §8.3, séparation des tâches). */
  moi: string;
  onFait: (msg: string) => void; onErreur: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [motif, setMotif] = useState<string | null>(null);
  const [dateApp, setDateApp] = useState(() => aujourdhuiIso());
  const etat = circuit.etat || 'approuvee';
  if (etat === 'approuvee' && !isRQ) return null;

  const appeler = async (corps: Record<string, unknown>) => {
    setBusy(true);
    try {
      const r = await fetchEcriture('/api/ged/ged/processus/circuit', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corps),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { onErreur(j?.error?.message || 'Acte refusé.'); return false; }
      onFait(String(j?.message || 'Fait.'));
      return true;
    } catch {
      onErreur('Acte impossible — le serveur n’a pas répondu.');
      return false;
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      padding: '9px 12px', borderRadius: 'var(--radius-md)',
      background: etat === 'brouillon' ? 'var(--amber-50)' : 'var(--gray-25)',
      border: `1px solid ${etat === 'brouillon' ? 'var(--amber-500)' : 'var(--border-default)'}`,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12.5, fontWeight: etat === 'brouillon' ? 700 : 400 }}>
          Cartographie des processus — version {circuit.version} · {ETAT_CARTO_LABEL[etat]}
          {etat === 'brouillon' && ' — modifications non validées'}
          {etat === 'approuvee' && circuit.dateApplication && ` · applicable le ${circuit.dateApplication}`}
        </span>
        <span style={{ flex: 1 }} />
        {/* ⚠️ ISO 15189 §8.3, MONTRÉ AVANT D'ÊTRE REFUSÉ (même correctif que
            l'organigramme) : proposer « Vérifier » à son propre rédacteur
            pour que le serveur le refuse seul ne dit rien de ce qui se
            passe réellement — la révision attend quelqu'un d'autre. */}
        {etat === 'brouillon' && (
          circuit.redacteur && circuit.redacteur === moi ? (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Rédigé par vous — en attente de vérification par quelqu’un d’autre.
            </span>
          ) : isRQ && (
            <Bouton taille="sm" disabled={busy} onClick={() => void appeler({ acte: 'verifier' })}>
              <I n="check" s={12} />Vérifier
            </Bouton>
          )
        )}
        {etat === 'averifier' && isRQ && (
          circuit.verifiePar && circuit.verifiePar === moi ? (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Vérifié par vous — en attente d’approbation par quelqu’un d’autre.
            </span>
          ) : (
            <>
              <input type="date" value={dateApp} aria-label="Date d’application"
                onChange={(e) => setDateApp(e.target.value)} style={{ ...inputStyle, width: 150 }} />
              <Bouton variante="primaire" taille="sm" disabled={busy} onClick={() => void appeler({ acte: 'approuver', dateApplication: dateApp })}>
                <I n="check" s={12} />Approuver
              </Bouton>
            </>
          )
        )}
        {/* LE RENVOI répond à « et si ça ne lui plaît pas ? » : le
            vérificateur (ou le RQ) rend la révision en rédaction, motif
            obligatoire — déjà câblé, pas une fonction à ajouter. */}
        {etat !== 'brouillon' && etat !== 'approuvee' && isRQ && (
          <Bouton taille="sm" disabled={busy} onClick={() => setMotif('')}>
            Renvoyer en rédaction
          </Bouton>
        )}
      </div>
      {motif !== null && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input value={motif} autoFocus placeholder="Ce qui doit être repris — obligatoire"
            aria-label="Motif du renvoi" style={{ ...inputStyle, flex: 1 }} onChange={(e) => setMotif(e.target.value)} />
          <Bouton taille="sm" onClick={() => setMotif(null)}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" disabled={busy || !motif.trim()} onClick={async () => { if (await appeler({ acte: 'renvoyer', motif })) setMotif(null); }}>
            Renvoyer
          </Bouton>
        </div>
      )}
    </div>
  );
}

/** Une carte de processus, ÉDITABLE et déplaçable — le drag ne porte QUE sur
 *  la poignée : le champ nom, les listes pilote/suppléant restent cliquables
 *  au clavier comme à la souris sans déclencher un glissement. */
function CarteProcessusEdit({ p, couleur, personnel, onPatch, onRemove }: {
  p: ProcRow; couleur: string; personnel: { id: string; nom: string }[];
  onPatch: (patch: Partial<ProcRow>) => void; onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform), transition,
    opacity: isDragging ? 0.4 : 1, ...card, padding: 12, borderTop: `3px solid ${couleur}`,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Bouton taille="sm" {...attributes} {...listeners}
          aria-label={`Réordonner ou déplacer « ${p.name || 'ce processus'} »`}
          title="Glisser : réordonner dans la bande, ou déposer sur une autre bande pour l’y déplacer"
          style={{ cursor: 'grab', touchAction: 'none' }}>
          <I n="grip" s={13} />
        </Bouton>
        {/* ── L'ABRÉVIATION SE RÈGLE AILLEURS, ET À UN SEUL ENDROIT ──────
            (RQ, 07/09/2026 : « l'abréviation est gérée à deux endroits
            différents, ne laisser que le paramétrage ».)

            Elle se saisissait ici ET au paramétrage → Codification, sur le même
            champ `processus[].codif`. Deux portes sur une valeur qui ENTRE DANS
            LA RÉFÉRENCE des documents : celui qui la change ici ne voit pas
            qu'il renomme les prochains codes, et celui qui la lit là-bas ne sait
            pas qu'elle a bougé.

            ⚠️ Elle reste AFFICHÉE : la masquer obligerait à ouvrir un autre
            écran pour savoir quel processus porte quel code — c'est justement
            ce qu'on vient chercher ici. Elle est montrée, pas modifiable. */}
        <span title="L’abréviation se règle au Paramétrage → Codification"
          style={{ width: 56, flex: 'none', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, color: p.codif ? 'var(--text-muted)' : 'var(--text-faint)', border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '5px 4px' }}>
          {p.codif || '—'}
        </span>
        <input value={p.name} placeholder="Nom du processus" onChange={(e) => onPatch({ name: e.target.value })}
          style={{ ...inputStyle, flex: 1, padding: '5px 7px' }} />
        <Bouton taille="sm" onClick={onRemove} title="Retirer" aria-label={`Retirer « ${p.name || 'ce processus'} »`}><I n="trash" s={13} /></Bouton>
      </div>
      <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
        <select value={p.pilote || ''} onChange={(e) => onPatch({ pilote: e.target.value })} style={{ ...inputStyle, padding: '5px 7px' }}>
          <option value="">Pilote…</option>
          {personnel.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
        </select>
        <select value={p.suppleant || ''} onChange={(e) => onPatch({ suppleant: e.target.value })} style={{ ...inputStyle, padding: '5px 7px' }}>
          <option value="">Suppléant…</option>
          {personnel.filter((u) => u.id !== p.pilote).map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-muted)' }}>
          <input type="checkbox" checked={p.active !== false} onChange={(e) => onPatch({ active: e.target.checked })}
            style={{ accentColor: 'var(--color-primary)' }} />actif
        </label>
      </div>
    </div>
  );
}

/** La bande d'une catégorie — DROPPABLE même vide (sinon rien n'accepte un
 *  glissement quand la dernière carte quitte Support). */
function BandeDroppable({ id, enfants }: { id: string; enfants: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 12, minHeight: 88,
      borderRadius: 'var(--radius-md)', outline: isOver ? '2px dashed var(--color-primary)' : 'none', outlineOffset: 4,
    }}>
      {enfants}
    </div>
  );
}

/** Compteur CLIQUABLE d'une carte de processus. */
function BoutonCompteur({ n, libelle, titre, alerte, onOuvrir }: {
  n: number; libelle: string; titre: string; alerte?: boolean; onOuvrir: () => void;
}) {
  // Zéro n'ouvre rien : un bouton qui mène à une liste vide use la confiance
  // dans tous les autres. Il reste LU, simplement pas cliquable.
  if (!n) return <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>aucun {libelle}</span>;
  return (
    <button type="button" onClick={onOuvrir} title={titre}
      className="ged-compteur" data-alerte={alerte ? '1' : undefined}
      style={{
        border: 'none', cursor: 'pointer',
        fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700,
        color: alerte ? 'var(--amber-600)' : 'var(--color-primary-active)',
      }}>
      {n} {libelle}
    </button>
  );
}

type FiltreProc = {
  proc: string;
  quoi: 'docs' | 'ncs';
  lignes: { cle: string; ref?: string; titre: string; statut: string }[];
} | null;

/**
 * Ce que compte une carte, ouvert sans quitter la cartographie.
 *
 * Les deux compteurs d'une carte — documents rattachés, NC ouvertes — étaient
 * du texte mort. Or la question qu'on se pose en les lisant est « lesquels ? »,
 * et il fallait aller filtrer le registre à la main pour y répondre. Chaque
 * ligne ouvre sa fiche en PANNEAU superposé : on garde la cartographie sous les
 * yeux, ce qui est justement ce qu'on était en train d'examiner.
 */
function ListeDuProcessus({ filtre, onFermer }: { filtre: FiltreProc; onFermer: () => void }) {
  const { ouvrirPanneau, openDoc } = useGed();
  useEscape(!!filtre, onFermer);
  if (!filtre) return null;
  const estDoc = filtre.quoi === 'docs';
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden', borderInlineStart: '3px solid var(--color-primary)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-subtle)' }}>
        <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>
          {filtre.lignes.length} {estDoc ? 'document(s)' : 'non-conformité(s) ouverte(s)'} — {filtre.proc}
        </strong>
        <span style={{ flex: 1 }} />
        <Bouton taille="sm" type="button" onClick={onFermer}>Fermer</Bouton>
      </div>
      {filtre.lignes.map((l) => (
        <button key={l.cle} type="button" className="ged-row"
          onClick={() => (estDoc ? openDoc(l.cle) : ouvrirPanneau({ type: 'nc', valeur: l.cle, origine: filtre.proc }))}
          style={{ display: 'flex', gap: 10, alignItems: 'baseline', width: '100%', textAlign: 'start', background: 'none', border: 'none', borderTop: '1px solid var(--border-subtle)', padding: '10px 14px', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)', minWidth: 132 }}>{l.ref || l.cle}</span>
          <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-strong)' }}>{l.titre}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{l.statut}</span>
        </button>
      ))}
    </div>
  );
}

// Cartographie des processus du SMQ.
export function Processus() {
  const { settings, docs, ncs, saveSettings, session, loading, booted, smq, showToast } = useGed();
  const [filtre, setFiltre] = useState<FiltreProc>(null);
  const isRQ = session.roles.includes('rq');
  /* ⚠️ `DEFAULT_PROCESSUS` NE PORTE AUCUN PILOTE — c'est une liste de noms, pas
     une cartographie. Servi tel quel pendant le chargement, il affichait douze
     cartes « Pilote : non désigné · aucun document » (relevé BLOQ-03) : non pas
     une page en attente, mais un CONSTAT D'AUDIT inventé, en ambre, sur un
     écran qui doit justement prouver que chaque processus a un pilote nommé.
     Le repli garde son rôle — une cartographie jamais paramétrée — mais on ne
     le montre plus tant qu'on ne sait pas s'il y en a une. */
  const attend = enChargement(booted, loading, settings?.processus?.length || 0, docs.length, ncs.length);
  const list: ProcRow[] = (settings?.processus && settings.processus.length) ? settings.processus : DEFAULT_PROCESSUS;
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState<ProcRow[]>([]);
  const [saving, setSaving] = useState(false);

  const orphanNC = ncs.filter((n) => n.processus && !list.some((p) => sameProc(p.name, n.processus)));
  const orphanLabels = Array.from(new Set(orphanNC.map((n) => n.processus)));

  /* Versions des réglages au moment où le brouillon a été pris (14/10/2026) :
     le serveur refuse l'enregistrement si la cartographie a bougé depuis. */
  const versionsBase = React.useRef<Record<string, string> | undefined>(undefined);
  const startEdit = () => { versionsBase.current = settings?.versions; setDraft(JSON.parse(JSON.stringify(list))); setEdit(true); };
  const patchP = (id: string, p: Partial<ProcRow>) => setDraft((d) => d.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const addP = () => setDraft((d) => [...d, { id: `p_${Date.now().toString(36)}`, codif: '', name: '', group: 'realisation', pilote: '', active: true }]);
  const removeP = (id: string) => setDraft((d) => d.filter((x) => x.id !== id));

  /**
   * ── LE RÉORDONNANCEMENT AU GLISSER (Lot 6, 05/09/2026) ────────────────────
   * `draft` reste un tableau À PLAT, mélangeant les trois bandes — c'est CET
   * ordre qui fait l'ordre d'affichage (commentaire d'origine, toujours vrai).
   * Réordonner DANS une bande ne doit toucher QUE les emplacements (index) que
   * cette bande occupe dans le tableau à plat : les items des deux autres
   * bandes doivent rester exactement à leur place, sinon glisser une carte de
   * Support déplacerait aussi, en silence, des cartes de Management posées
   * entre deux emplacements de Support dans le tableau.
   */
  const reordonnerBande = (groupe: string, idDeplace: string, idCible: string) => setDraft((d) => {
    const indicesBande = d.map((x, i) => (x.group === groupe ? i : -1)).filter((i) => i >= 0);
    const itemsBande = indicesBande.map((i) => d[i]);
    const ancien = itemsBande.findIndex((x) => x.id === idDeplace);
    const nouveau = itemsBande.findIndex((x) => x.id === idCible);
    if (ancien === -1 || nouveau === -1 || ancien === nouveau) return d;
    const reordonnes = arrayMove(itemsBande, ancien, nouveau);
    const copie = [...d];
    indicesBande.forEach((flatIdx, k) => { copie[flatIdx] = reordonnes[k]; });
    return copie;
  });

  /** Le changement DE BANDE — gouverné, hors brouillon (Lot 6 §Gouvernance,
   *  même exigence que le Lot 4) : confirmation nommant la conséquence, motif
   *  obligatoire, trace, révision du document. Écrit directement au serveur,
   *  comme le re-rattachement de l'organigramme — pas d'attente d'un
   *  « Enregistrer » qui pourrait ne jamais venir sur un geste de cette
   *  gravité. */
  const [confirmBande, setConfirmBande] = useState<{ id: string; nom: string; ancienGroupe: string; versGroupe: string } | null>(null);
  const [motifBande, setMotifBande] = useState('');
  const [envoiBande, setEnvoiBande] = useState(false);
  const libelleGroupe = (g: string) => PROCESS_GROUPS.find((x) => x.key === g)?.title || g;
  const confirmerDeplacementBande = async () => {
    if (!confirmBande || !motifBande.trim()) return;
    setEnvoiBande(true);
    const ok = await smq(
      'ged/processus/deplacer-bande',
      { id: confirmBande.id, versGroupe: confirmBande.versGroupe, motif: motifBande.trim() },
      `« ${confirmBande.nom} » déplacé vers ${libelleGroupe(confirmBande.versGroupe)}.`,
    );
    setEnvoiBande(false);
    if (ok) {
      // Le serveur vient d'écrire directement `settings.processus` : le
      // brouillon local doit suivre, sinon un « Enregistrer » plus tard
      // écraserait ce geste avec l'ancienne bande encore en mémoire.
      setDraft((d) => d.map((x) => (x.id === confirmBande.id ? { ...x, group: confirmBande.versGroupe } : x)));
      // …et la version de base avance avec lui : c'est notre propre geste.
      const fraiches = useGed.getState().settings?.versions;
      if (fraiches?.processus) versionsBase.current = { ...(versionsBase.current || {}), processus: fraiches.processus };
      setConfirmBande(null);
      setMotifBande('');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    const item = draft.find((x) => x.id === activeId);
    if (!item) return;
    // Déposé sur la bande elle-même (zone vide) → l'id du conteneur porte
    // directement la bande. Déposé sur une carte → on hérite de SA bande.
    const groupeDestination = overId.startsWith('bande-')
      ? overId.slice('bande-'.length)
      : draft.find((x) => x.id === overId)?.group;
    if (!groupeDestination) return;
    if (groupeDestination === item.group) {
      if (activeId !== overId) reordonnerBande(item.group, activeId, overId);
      return;
    }
    // Changement DE BANDE : jamais silencieux (spec — « un processus qui
    // change de catégorie change de nature dans le SMQ »).
    setConfirmBande({ id: activeId, nom: item.name || activeId, ancienGroupe: item.group, versGroupe: groupeDestination });
    setMotifBande('');
  };

  const saveProc = async () => {
    setSaving(true);
    const clean = draft.filter((p) => p.name.trim()).map((p) => ({ ...p, name: p.name.trim() }));
    const ok = await saveSettings({ processus: clean } as never, versionsBase.current);
    setSaving(false);
    if (ok) setEdit(false);
  };
  // Même garde que l'organigramme : le brouillon porte toute la cartographie,
  // réordonnancement compris — et l'ordre de la liste EST l'ordre d'affichage,
  // donc un abandon involontaire perd aussi un travail de classement invisible
  // dans les champs.
  const dirty = edit && JSON.stringify(draft) !== JSON.stringify(list);
  const { demander, confirmation } = useConfirmation();
  const annuler = React.useCallback(async () => {
    if (dirty && !(await demander('Des modifications non enregistrées seront perdues. Quitter l’édition ?', { confirmer: 'Quitter sans enregistrer' }))) return;
    setEdit(false);
  }, [dirty, demander]);
  useEscape(edit && !saving, annuler);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <H1 eyebrow={<>Cartographie SMQ<Norme art="5.4" /></>} title="Processus" />
        {isRQ && !edit && <Bouton taille="sm" onClick={startEdit}><I n="pen" s={13} />Modifier la cartographie</Bouton>}
        {isRQ && edit && (
          <PiedActions taille="sm" annuler={{ onClick: annuler }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: saving, disabled: saving, onClick: saveProc }} />
        )}
      </div>

      {!edit && PROCESS_GROUPS.map((g) => {
        const items = list.filter((p) => p.group === g.key && p.active !== false);
        if (!items.length) return null;
        return (
          <div key={g.key}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 8 }}>{g.title}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 12 }}>
              {attend ? <SkeletonTiles n={items.length || 3} /> : items.map((p) => {
                // Comparaison normalisée (casse/accents/espaces) : le `===` strict
                // ne comptait rien dès qu'un caractère différait, en silence.
                // La garde réelle est en amont (menu + serveur contraints à cette
                // taxonomie) ; ceci couvre les données antérieures. QA v2 BUG-03.
                /* ⚠️ Les documents se comptaient par `discipline`, héritage
                   d'avant la séparation des deux axes (14/08/2026) : depuis,
                   26 documents sur 27 portent un PROCESSUS et plus aucune
                   discipline, si bien que toutes les cartes affichaient
                   « 0 document ». Le repli sur `discipline` reste, pour les
                   fiches que la reprise n'aurait pas touchées. */
                const docsDuProc = docs.filter((d) => sameProc(d.processus || d.discipline, p.name));
                const ncsDuProc = ncs.filter((n) => sameProc(n.processus, p.name) && n.statut !== 'cloturee');
                const nDocs = docsDuProc.length;
                const nNC = ncsDuProc.length;
                return (
                  <div key={p.id} style={{ ...card, padding: 16, borderTop: `3px solid ${g.color}` }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      {p.codif && (
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-on-primary)', background: g.color, borderRadius: 'var(--radius-sm)', padding: '2px 7px', flex: 'none' }}>{p.codif}</span>
                      )}
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>{p.name}</span>
                    </div>
                    {/* Pilote nommé : exigence ISO 15189:2022 §4 / §5.2 */}
                    <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 7 }}>
                      Pilote : <strong>{p.pilote ? userName(settings, p.pilote) : <span style={{ color: 'var(--amber-600)' }}>non désigné</span>}</strong>
                      {p.note && <span style={{ color: 'var(--amber-600)' }}> · {p.note}</span>}
                    </div>
                    {/* Un processus dont le pilote s'absente sans suppléant
                        désigné est un constat d'audit : on le dit ici plutôt
                        que de laisser la case vide passer pour un oubli d'écran. */}
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                      Suppléant : {p.suppleant ? userName(settings, p.suppleant) : <span style={{ color: 'var(--amber-600)' }}>non désigné</span>}
                    </div>
                    {/* Les deux compteurs OUVRENT ce qu'ils comptent : c'est la
                        question qu'on se pose en les lisant — « lesquels ? ».
                        Ils étaient jusqu'ici du texte mort, et il fallait aller
                        rechercher le processus à la main dans les deux
                        registres. Les fiches s'ouvrent en PANNEAU superposé :
                        on ne quitte pas la cartographie qu'on est en train de
                        lire. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                      <BoutonCompteur
                        n={nDocs} libelle={`document${nDocs > 1 ? 's' : ''}`}
                        titre={`Voir les ${nDocs} document(s) rattachés à « ${p.name} »`}
                        onOuvrir={() => { setFiltre({ proc: p.name, quoi: 'docs', lignes: docsDuProc.map((d) => ({ cle: d.code, titre: d.titre, statut: d.statut })) }); }}
                      />
                      <span style={{ color: 'var(--text-faint)' }}>·</span>
                      <BoutonCompteur
                        n={nNC} libelle={`NC ouverte${nNC > 1 ? 's' : ''}`} alerte={nNC > 0}
                        titre={nNC ? `Voir les ${nNC} non-conformité(s) ouvertes de « ${p.name} »` : 'Aucune non-conformité ouverte sur ce processus'}
                        onOuvrir={() => { setFiltre({ proc: p.name, quoi: 'ncs', lignes: ncsDuProc.map((n) => ({ cle: n.documentId, ref: n.ref, titre: n.titre, statut: n.statut })) }); }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* NC rattachées à un processus absent de la cartographie : elles ne sont
          comptées dans aucune carte. Le dire plutôt que de les perdre en
          silence (la cartographie a changé le 22/07 pour les processus A→L). */}
      <ListeDuProcessus filtre={filtre} onFermer={() => setFiltre(null)} />

      {!edit && orphanNC.length > 0 && (
        <div style={{ ...card, padding: '14px 16px', borderInlineStart: '3px solid var(--amber-500)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{orphanNC.length} non-conformité(s) hors cartographie</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.5 }}>
            Elles pointent vers un processus qui n’existe plus dans la cartographie : {orphanLabels.join(' · ')}. Rattachez-les à un processus A→L depuis l’écran Non-conformités.
          </div>
        </div>
      )}

      {/* ── LA CARTOGRAPHIE ÉDITABLE, EN TROIS BANDES (Lot 6, 05/09/2026) ──────
          `dnd-kit` SEUL — spec §I.3 : la cartographie est une liste ordonnée en
          trois bandes, pas une hiérarchie, `d3-org-chart` n'y a pas sa place.
          Glisser dans une bande réordonne ; glisser vers une autre bande ouvre
          la confirmation gouvernée (ci-dessous). */}
      {edit && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <div style={{ display: 'grid', gap: 18 }}>
            {PROCESS_GROUPS.map((g) => {
              const items = draft.filter((p) => p.group === g.key);
              return (
                <div key={g.key}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 8 }}>
                    {g.title} <span style={{ fontWeight: 400, color: 'var(--text-faint)' }}>({items.length})</span>
                  </div>
                  <SortableContext items={items.map((p) => p.id)} strategy={rectSortingStrategy}>
                    <BandeDroppable
                      id={`bande-${g.key}`}
                      enfants={items.length ? items.map((p) => (
                        <CarteProcessusEdit key={p.id} p={p} couleur={g.color}
                          personnel={personnelReel(settings?.gedUsers || [])}
                          onPatch={(patch) => patchP(p.id, patch)} onRemove={() => removeP(p.id)} />
                      )) : (
                        <span style={{ fontSize: 12.5, color: 'var(--text-faint)', gridColumn: '1 / -1', padding: '10px 2px' }}>
                          Aucun processus — déposez-en un ici pour le classer {g.title.toLowerCase()}.
                        </span>
                      )}
                    />
                  </SortableContext>
                </div>
              );
            })}
          </div>
          <Bouton taille="sm" onClick={addP} style={{ marginTop: 14 }}><I n="plus" s={13} />Ajouter un processus</Bouton>
        </DndContext>
      )}

      {/* ── LA CONFIRMATION DU CHANGEMENT DE BANDE — jamais silencieuse ────────
          « La confirmation doit énoncer la conséquence, pas seulement
          l'action » (spec, point de vigilance métier). */}
      {confirmBande && (
        <Modale
          titre="Confirmer le changement de bande"
          sousTitre={`Déplacer « ${confirmBande.nom} » de ${libelleGroupe(confirmBande.ancienGroupe)} vers ${libelleGroupe(confirmBande.versGroupe)} ?`}
          onClose={() => { if (!envoiBande) { setConfirmBande(null); setMotifBande(''); } }}
          pied={<>
            <Bouton taille="sm" disabled={envoiBande} onClick={() => { setConfirmBande(null); setMotifBande(''); }}>Annuler</Bouton>
            <Bouton variante="primaire" taille="sm" disabled={envoiBande || !motifBande.trim()} onClick={() => void confirmerDeplacementBande()}>
              {envoiBande ? 'Enregistrement…' : 'Confirmer'}
            </Bouton>
          </>}
        >
          <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
            Un processus qui change de bande <strong>change de nature dans le SMQ</strong> — ce n’est pas une
            préférence d’affichage. Ce geste ouvre (ou poursuit) une révision de la cartographie, et sera
            tracé au journal avec le motif ci-dessous.
          </p>
          <label htmlFor="motif-bande" style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4 }}>
            Motif — obligatoire
          </label>
          <textarea id="motif-bande" autoFocus rows={3} value={motifBande}
            onChange={(e) => setMotifBande(e.target.value)}
            placeholder="Ce qui justifie ce changement de bande"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--font-sans)', width: '100%' }} />
        </Modale>
      )}

      <CircuitCartographie
        circuit={settings?.codification?.orgCircuit?.cartographie || CIRCUIT_CARTO_DEFAUT}
        isRQ={isRQ}
        moi={session.name}
        onFait={(msg) => showToast(msg)}
        onErreur={(msg) => showToast(msg, 'erreur')}
      />

      {confirmation}
    </div>
  );
}
